import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { EyeIcon, EyeOffIcon, PhoneIcon, CheckCircle2Icon, StarIcon, ZapIcon, ShieldCheckIcon, MessageSquareIcon } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { rsAnalytics } from "@/lib/rudderstack";
import { trackClientEvent } from "@/lib/lifecycleEvents";
import { Turnstile, turnstileEnabled } from "@/components/Turnstile";
import { useFingerprintData } from "@/hooks/useFingerprintData";
import { TRIAL_DAYS } from "@shared/onboardingSteps";
import { CALL_CONSENT_TEXT_VERSION } from "@shared/callConsent";
import { TrialShell, StepHead } from "@/pages/onboarding/trial/TrialShell";
import { browserStores, captureTrialLink, carriedNumber } from "@/lib/trialDeepLink";

/**
 * The call-consent box has to survive the round trip through Google: the
 * choice is made here, the account is created when Google sends the person
 * back with a grant. Same-tab sessionStorage carries it; read once, then
 * cleared.
 */
const CALL_CONSENT_KEY = 'lp_call_consent';
/** The two signup questions, carried across the Google redirect like consent. */
const QUALIFICATION_KEY = 'lp_signup_qualification';

/**
 * First-touch attribution. /api/auth/register has accepted these keys all
 * along and this page never sent them, so every signup_session's "how did
 * they land" was empty. Captured on the FIRST load only and kept in same-tab
 * sessionStorage, because the second load (back from the OTP email or from
 * Google) has lost the query string and has Google as its referrer.
 */
const FIRST_TOUCH_KEY = 'lp_first_touch';
type FirstTouch = Partial<Record<
  'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent' | 'landingPage' | 'referrer', string>>;
function captureFirstTouch(): void {
  try {
    if (sessionStorage.getItem(FIRST_TOUCH_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    // The Google return carries a signed grant in the URL — never store that
    // as a landing page, and it is not a first touch anyway.
    if (params.get('googleGrant')) return;
    const pick = (k: string, max = 200) => (params.get(k) || '').slice(0, max) || undefined;
    const touch: FirstTouch = {
      utmSource: pick('utm_source'),
      utmMedium: pick('utm_medium'),
      utmCampaign: pick('utm_campaign'),
      utmTerm: pick('utm_term'),
      utmContent: pick('utm_content'),
      landingPage: (window.location.pathname + window.location.search).slice(0, 500),
      referrer: (document.referrer || '').slice(0, 500) || undefined,
    };
    sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch { /* private mode: no attribution, never a failed signup */ }
}
/**
 * "Get free credits" referral: /signup?invite=<CODE> is kept in localStorage
 * (it must survive the OTP email and the Google round trip) and sent to
 * /api/auth/register as referralCode, which records the referral.
 */
const REFERRAL_KEY = 'lp_referral_code';
function captureReferralCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('invite');
    if (code && /^[A-Za-z0-9]{4,16}$/.test(code)) localStorage.setItem(REFERRAL_KEY, code.toUpperCase());
  } catch { /* storage blocked: no referral, never a failed signup */ }
}
function referralField(): { referralCode?: string } {
  try {
    const code = localStorage.getItem(REFERRAL_KEY);
    return code ? { referralCode: code } : {};
  } catch { return {}; }
}
function firstTouch(): FirstTouch {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: FirstTouch = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string' && v) (out as Record<string, string>)[k] = v;
    }
    return out;
  } catch { return {}; }
}

/** The grant's claims. base64url and UTF-8 aware — a plain atob() mangles
 *  "José" and throws on '-'/'_'. Read only; the server verifies the signature. */
function grantClaims(grant: string): Record<string, any> {
  try {
    const b64 = grant.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) || {};
  } catch { return {}; }
}
/** Mirrors server/lib/nameValidation checkPersonName: the server 400s a
 *  supplied name that fails it, and a Google profile name must never be the
 *  reason a Google signup fails — so an implausible one is simply not sent. */
const HAS_LETTER = new RegExp('\\p{L}', 'u');
const HAS_DIGIT = new RegExp('\\p{Nd}', 'u');
function plausibleName(v: unknown): string | undefined {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s || s.length > 80 || !HAS_LETTER.test(s) || HAS_DIGIT.test(s) || s.includes('@') || /https?:\/\/|www\./i.test(s)) return undefined;
  return s;
}

const registerSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  companyName: z.string().optional(),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function Register() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    rsAnalytics.track('signup_page_viewed');
    captureFirstTouch();
    captureReferralCode();

    // Lifecycle: signup.started (typed)
    try {
      const params = new URLSearchParams(window.location.search);
      const country_guess =
        (typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        '';
      trackClientEvent('signup.started', {
        signup_source: params.get('source') || 'organic',
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null,
        utm_term: params.get('utm_term') || null,
        utm_content: params.get('utm_content') || null,
        country_guess,
      });
    } catch {
      // never throw from telemetry
    }
  }, []);
  
  // The "temporarily disable trial signup" redirect that used to live here is
  // gone: the Quo-parity onboarding wizard is the front door again, so this is
  // a real entry point rather than only the team-invite acceptance page.
  // Every "Start Free Trial" CTA on the pricing and login pages lands here.

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Team-invite context resolved from the ?token= param (null for normal signup).
  const [invite, setInvite] = useState<{ token: string; email: string; role: string; organizationName: string } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [emailValidation, setEmailValidation] = useState<{
    valid: boolean;
    disposable: boolean;
    checking: boolean;
    message?: string;
  } | null>(null);
  const { toast } = useToast();
  
  // Check if user selected a phone number from landing page
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const [, setCaptchaToken] = useState<string | null>(null);
  const [, setCaptchaBroken] = useState(false);
  // The widget is interaction-only (invisible unless Cloudflare needs a click),
  // so a fast click can beat the background check. Submit handlers read the
  // token through this ref and wait for it briefly instead of refusing.
  const captchaTokenRef = useRef<string | null>(null);
  const captchaBrokenRef = useRef(false);
  // A token arriving means the widget works, even after an earlier error.
  const onCaptchaToken = (t: string | null) => {
    captchaTokenRef.current = t;
    if (t) captchaBrokenRef.current = false;
    setCaptchaToken(t);
  };
  // Tokens are single-use: after a refused submit, fetch a fresh one.
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const resetCaptcha = () => { captchaTokenRef.current = null; setCaptchaResetKey((k) => k + 1); };
  const onCaptchaError = () => { captchaBrokenRef.current = true; setCaptchaBroken(true); };
  const [captchaWaiting, setCaptchaWaiting] = useState(false);
  const waitForCaptcha = async (ms = 8000): Promise<string | null> => {
    if (captchaTokenRef.current) return captchaTokenRef.current;
    setCaptchaWaiting(true);
    try {
      const until = Date.now() + ms;
      while (!captchaTokenRef.current && !captchaBrokenRef.current && Date.now() < until) {
        await new Promise((r) => setTimeout(r, 150));
      }
      return captchaTokenRef.current;
    } finally {
      setCaptchaWaiting(false);
    }
  };

  // The device signal this form has never sent. `deviceFingerprint` has been
  // accepted and persisted by /api/auth/register all along (server/auth.ts),
  // and /auth/free-signup has always supplied it — but this page, which is the
  // one the login screen links to, did not. That is why the column is populated
  // on none of the accounts we have. FingerprintJS Pro is already mounted for
  // every non-admin page and already paid for; this just stops throwing away
  // what it collects.
  const fingerprintData = useFingerprintData();
  
  // Load selected number from sessionStorage on mount
  useEffect(() => {
    const number = sessionStorage.getItem('selectedPhoneNumber');
    const country = sessionStorage.getItem('selectedCountry');
    if (number) {
      setSelectedNumber(number);
      setSelectedCountry(country);
    }
    
    // Capture and store the signup URL for tracking purposes
    const existingSignupUrl = localStorage.getItem('signup_url');
    if (!existingSignupUrl) {
      const signupUrl = window.location.pathname;
      localStorage.setItem('signup_url', signupUrl);
      console.log('📍 [REGISTER] Captured signup URL:', signupUrl);
    }
  }, []);

  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Resolve a team-invite token: fetch the invited email + team so we can
  // pre-fill and lock the email and show what the user is joining.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/invite/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (res.ok && data.valid) {
          setInvite({ token, email: data.email, role: data.role, organizationName: data.organizationName });
          form.setValue('email', data.email);
        } else {
          setInviteError(data.message || 'This invitation is invalid or has expired.');
        }
      } catch {
        setInviteError('Could not load this invitation. Please try again.');
      }
    })();
  }, [form]);

  // Debounced email validation
  useEffect(() => {
    console.log('🔵 [EMAIL-VALIDATION] useEffect initialized');
    let timer: NodeJS.Timeout;
    
    try {
      const subscription = form.watch((value, { name }) => {
        console.log('🔵 [EMAIL-VALIDATION] form.watch triggered', { name, value: value.email });
        
        if (name !== 'email') {
          console.log('🔵 [EMAIL-VALIDATION] Skipping - not email field');
          return;
        }
        
        clearTimeout(timer);
        
        const email = value.email;
        console.log('🔵 [EMAIL-VALIDATION] Email value:', email);
        
        if (!email || !email.includes('@')) {
          console.log('🔵 [EMAIL-VALIDATION] Email incomplete, clearing validation');
          setEmailValidation(null);
          return;
        }

        console.log('🔵 [EMAIL-VALIDATION] Setting timer for validation');
        timer = setTimeout(async () => {
          try {
            console.log('🔵 [EMAIL-VALIDATION] Starting API call for:', email);
            setEmailValidation({ valid: true, disposable: false, checking: true });
            const response = await apiRequest('POST', '/api/auth/validate-email', { email });
            console.log('🔵 [EMAIL-VALIDATION] API response:', response);
            setEmailValidation({
              valid: response.valid,
              disposable: response.disposable,
              checking: false,
              message: response.message
            });
          } catch (error) {
            console.error('🔴 [EMAIL-VALIDATION] API Error:', error);
            setEmailValidation({ valid: true, disposable: false, checking: false });
          }
        }, 500);
      });
      
      console.log('🔵 [EMAIL-VALIDATION] Subscription created successfully');
      
      return () => {
        console.log('🔵 [EMAIL-VALIDATION] Cleanup running');
        clearTimeout(timer);
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('🔴 [EMAIL-VALIDATION] useEffect Error:', error);
    }
  }, [form]);

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterForm) => {
      console.log('🔄 [REGISTRATION] Starting registration process:', {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName || 'Not provided'
      });
      
      const { confirmPassword, ...registerData } = data;
      
      try {
        console.log('📤 [REGISTRATION] Sending request to /api/auth/register');
        const response = await apiRequest('POST', '/api/auth/register', { ...registerData, ...referralField() });
        console.log('✅ [REGISTRATION] Registration successful:', {
          hasToken: !!response.token,
          tokenLength: response.token?.length,
          user: response.user,
          organization: response.organization
        });
        return response;
      } catch (error: any) {
        console.error('❌ [REGISTRATION] Registration failed:', error);
        console.error('❌ [REGISTRATION] Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('🎉 [REGISTRATION] Processing successful registration:', data);
      
      if (!data.token) {
        console.error('❌ [REGISTRATION] No token received in response');
        toast({
          title: "Registration Error",
          description: "No authentication token received",
          variant: "destructive",
        });
        return;
      }
      
      localStorage.setItem('token', data.token);
      console.log('💾 [REGISTRATION] Token saved to localStorage');
      
      // Track signup event
      if (data.user && data.organization) {
        analytics.trackSignup(
          data.user.id,
          data.organization.id,
          'email'
        );
        rsAnalytics.track('signup_completed', {
          userId: data.user.id,
          organizationId: data.organization.id,
          method: 'email'
        });
      }
      
      // Invitees are pre-verified (they clicked the tokenised invite sent to their
      // email) and skip the org-setup wizard — send them straight to their team's
      // dashboard. Everyone else verifies their email first.
      const joinedViaInvite = !!invite || !!data.organization?.joinedViaInvite;
      if (joinedViaInvite) {
        toast({ title: `Welcome to ${data.organization?.name || "the team"}!`, description: "You're all set." });
        console.log('🔀 [REGISTRATION] Invitee — skipping verification, going to dashboard');
        window.location.href = '/dashboard-selector';
        return;
      }

      toast({
        title: "Welcome to LimePhone",
        description: "Please check your email to verify your account.",
      });

      console.log('🔀 [REGISTRATION] Redirecting to email verification...');
      window.location.href = '/verify-email';
    },
    onError: (error: any) => {
      console.error('💥 [REGISTRATION] Mutation error handler:', error);
      resetCaptcha();

      // Accepting an invite but the email already has an account: they can't
      // register again — send them to log in, carrying the invite token so
      // login attaches their existing account to the inviting org.
      if (invite && (error?.status === 409 || error?.data?.code === 'USER_EXISTS_INVITE')) {
        toast({ title: "You already have an account", description: "Log in to join the team." });
        window.location.href = `/auth/login?token=${encodeURIComponent(invite.token)}`;
        return;
      }

      // Signup was refused by a fraud gate. The server sends a specific reason
      // ("turn off your VPN", "we can't verify this email"), but this page used
      // to show only the generic message — so a real customer on a corporate VPN
      // they cannot disable saw "Registration denied" with no explanation and no
      // way forward. Surface the actual reason and a route to support.
      const blockCode = error?.errorCode || error?.data?.errorCode || error?.data?.code;
      if (
        blockCode === 'SIGNUP_IP_BLOCKED' ||
        blockCode === 'SIGNUP_COUNTRY_BLOCKED' ||
        blockCode === 'SIGNUP_RISK_BLOCKED'
      ) {
        toast({
          title:
            blockCode === 'SIGNUP_IP_BLOCKED'
              ? "We couldn't verify your connection"
              : "We couldn't complete your signup",
          description:
            error?.data?.reason ||
            "Please contact support@limephone.io and we'll get you set up.",
          variant: "destructive",
          duration: 12000,
        });
        return;
      }

      toast({
        title: "Registration Failed",
        description: error.message || "Failed to create account. Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Passwordless signup ────────────────────────────────────────────────────
  // Signup asks for one thing: an email. Name and company are collected later
  // by the onboarding wizard (business_profile), and the password is set on its
  // final step. Invitees are deliberately left on the full form below: they
  // join an existing org rather than creating one, and that path already works.
  const [flow, setFlow] = useState<'choose' | 'email' | 'otp'>('choose');
  const [signupEmail, setSignupEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  // The signup screen asks for ONE thing: how they want to sign in. What the
  // business does, the no-cold-calling attestation and the optional call
  // consent moved to their own onboarding screens (2026-10-25, one action per
  // page): the attestation is now enforced by the SERVER before the trial
  // starts (lib/trialAttestation), not by this form's disabled button.
  // The number (and plan) a /free-signup link carried, kept across the OTP and
  // the Google round trip (lib/trialDeepLink). Shown so the one decision they
  // already made is visible; the number step re-checks it is still free.
  const [linkNumber] = useState(() => {
    const stores = browserStores();
    captureTrialLink(new URLSearchParams(window.location.search), stores);
    return carriedNumber(stores);
  });

  const finishSignup = (data: any) => {
    // 'token' — the key queryClient, useAuth and every other auth screen read.
    // This wrote 'auth_token', so verifying the OTP stored a session nothing
    // could see: the user landed on /onboarding unauthenticated and was bounced
    // straight back to signup.
    if (data?.token) localStorage.setItem('token', data.token);
    window.location.href = '/onboarding';
  };

  // Google returns here with a short-lived server-signed grant. Post it
  // straight through so the user never sees a form — the grant is what lets the
  // server skip the OTP, because Google already proved they own the address.
  const googleMutation = useMutation({
    mutationFn: async (grant: string) => {
      const claims = grantClaims(grant);
      const claimEmail = typeof claims.email === 'string' ? claims.email : '';
      // Google already told us their name and the grant carries it; posting
      // only the email left users.first_name as the "there" placeholder.
      const googleFirst = plausibleName(claims.firstName);
      const googleLast = plausibleName(claims.lastName);
      // A stored value ('1' or '0') means the box was on screen when they left
      // for Google — that is what `asked` records, independent of the answer.
      const [consented, consentAsked] = (() => {
        try {
          const raw = sessionStorage.getItem(CALL_CONSENT_KEY);
          sessionStorage.removeItem(CALL_CONSENT_KEY);
          return [raw === '1', raw !== null];
        } catch { return [false, false]; }
      })();
      const carried = (() => {
        try {
          const raw = sessionStorage.getItem(QUALIFICATION_KEY);
          sessionStorage.removeItem(QUALIFICATION_KEY);
          return raw ? JSON.parse(raw) : null;
        } catch { return null; }
      })();
      return apiRequest('POST', '/api/auth/register', {
        email: claimEmail,
        googleGrant: grant,
        signupSource: 'google',
        ...(googleFirst ? { firstName: googleFirst } : {}),
        ...(googleLast ? { lastName: googleLast } : {}),
        // Only a tab opened before 2026-10-25 still carries these (the boxes
        // were on the old signup form); new signups answer on their own screens.
        ...(consented ? { callConsent: true, callConsentVersion: CALL_CONSENT_TEXT_VERSION } : {}),
        ...(consentAsked ? { asked: { callConsent: true } } : {}),
        ...firstTouch(),
        ...referralField(),
        ...(carried && typeof carried.description === 'string'
          ? { qualification: { description: carried.description, noColdCalling: carried.noColdCalling === true } } : {}),
        ...(fingerprintData?.visitorId ? { deviceFingerprint: fingerprintData.visitorId } : {}),
      });
    },
    // A verified grant returns a session: no email code (owner decision
    // 2026-10-25). Only if the server did NOT honour the grant (expired,
    // email mismatch) does it fall back to the emailed code, like any email
    // signup — never skip it client-side.
    onSuccess: (data: any) => {
      if (data?.token) return finishSignup(data);
      if (data?.requiresVerification && data?.userId) {
        setPendingUserId(data.userId);
        if (data.email) setSignupEmail(data.email);
        setFlow('otp');
        toast({ title: 'Check your email', description: 'We sent you a 6-digit code to finish signing up.' });
        return;
      }
      finishSignup(data);
    },
    onError: (e: any) => {
      toast({ title: 'Google sign-up failed', description: e?.data?.reason || e?.data?.message || e?.message || 'Please try again.', variant: 'destructive' });
      setFlow('choose');
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const grant = params.get('googleGrant');
    if (grant) {
      googleMutation.mutate(grant);
      return;
    }

    // Legacy Google return: older builds sent the user back here as
    // ?email=…&provider=google with no grant, because the callback did not
    // create accounts. Landing on the chooser with those params in the URL and
    // no explanation is how "Continue with Google" looked like it silently
    // failed. A cached bundle or an in-flight redirect can still arrive this
    // way, so carry the address into the email step rather than dropping it.
    if (params.get('provider') === 'google') {
      const legacyEmail = params.get('email');
      if (legacyEmail) {
        setSignupEmail(legacyEmail);
        setFlow('email');
        toast({
          title: 'Almost there',
          description: "Confirm your email and we'll send you a sign-in code.",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEmailMutation = useMutation({
    mutationFn: async () =>
      apiRequest('POST', '/api/auth/register', {
        email: signupEmail.trim(),
        signupSource: 'register',
        ...firstTouch(),
        ...referralField(),
        ...(captchaTokenRef.current ? { captchaToken: captchaTokenRef.current } : {}),
        ...(fingerprintData?.visitorId ? { deviceFingerprint: fingerprintData.visitorId } : {}),
      }),
    onSuccess: (data: any) => {
      setPendingUserId(data.userId);
      setFlow('otp');
      toast({ title: 'Check your email', description: `We sent a 6-digit code to ${signupEmail.trim()}.` });
    },
    onError: (e: any) => {
      resetCaptcha();
      toast({
        title: "Couldn't send your code",
        // The blocklist and captcha refusals put the actionable half in `reason`
        // and only "Registration denied" in `message`.
        description: e?.data?.reason || e?.data?.message || e?.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const verifyMutation = useMutation({
    // `link` carries the userId + code from the one-tap link in the email.
    mutationFn: async (link?: { userId: string; code: string }) =>
      apiRequest('POST', '/api/auth/signup/verify', link
        ? { userId: link.userId, code: link.code, via: 'link' }
        : { userId: pendingUserId, code: otp }),
    onSuccess: finishSignup,
    onError: (e: any) => {
      toast({ title: 'That code did not work', description: e?.message || 'Please try again.', variant: 'destructive' });
      setOtp('');
    },
  });

  // Resend cooldown: without one, impatient double-taps sent several mails
  // and the user then typed the code from the wrong one.
  const RESEND_COOLDOWN_S = 30;
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const resendMutation = useMutation({
    mutationFn: async () => apiRequest('POST', '/api/auth/signup/resend', { userId: pendingUserId }),
    onSuccess: () => {
      setResendIn(RESEND_COOLDOWN_S);
      toast({ title: 'New code sent', description: 'Use the code in the newest email. It can take a minute; check spam or promotions too.' });
    },
    onError: (e: any) => toast({ title: "Couldn't resend", description: e?.message || 'Please try again.', variant: 'destructive' }),
  });

  // One-tap link from the verification email: /auth/register?verify_uid=…&verify_code=…
  // Strip the params from the address bar first so the code does not linger
  // in history, then submit exactly as if the code had been typed.
  const linkVerifyStarted = useRef(false);
  useEffect(() => {
    if (linkVerifyStarted.current) return;
    const params = new URLSearchParams(window.location.search);
    const uid = params.get('verify_uid');
    const code = params.get('verify_code');
    if (!uid || !code || !/^\d{6}$/.test(code)) return;
    linkVerifyStarted.current = true;
    params.delete('verify_uid');
    params.delete('verify_code');
    const rest = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    setPendingUserId(uid);
    setFlow('otp');
    verifyMutation.mutate({ userId: uid, code });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitEmail = async () => {
    const value = signupEmail.trim();
    if (!value || !value.includes('@')) {
      toast({ title: 'Enter your email', description: 'We need an email address to send your code to.', variant: 'destructive' });
      return;
    }
    // Same refusal as the full form: the server rejects a tokenless request
    // once enforcement is on, so submitting anyway just moves the rejection
    // somewhere the user cannot act on it.
    if (turnstileEnabled && !(await waitForCaptcha())) {
      toast({
        title: 'Verification needed',
        description: captchaBrokenRef.current
          ? "The verification check couldn't load — usually an ad blocker or privacy extension. Allow challenges.cloudflare.com and refresh."
          : 'Please complete the verification check below.',
        variant: 'destructive',
      });
      return;
    }
    startEmailMutation.mutate();
  };

  const onSubmit = async (data: RegisterForm) => {
    // A missing token is REFUSED by the server once this product is enforced,
    // so submitting without one would just move the rejection later and make it
    // incomprehensible. When the widget itself failed to load, say so and name
    // the usual cause rather than promising a submission the server will deny.
    if (turnstileEnabled && !(await waitForCaptcha())) {
      toast({
        title: "Verification needed",
        description: captchaBrokenRef.current
          ? "The verification check couldn't load — usually an ad blocker or privacy extension. Allow challenges.cloudflare.com and refresh."
          : "Please complete the verification check below.",
        variant: "destructive",
      });
      return;
    }

    // Block submission if email is disposable — but NOT for invitees: their email
    // is fixed by the invitation and the server skips the business-email gate.
    if (!invite && emailValidation && !emailValidation.valid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.message || "Please use a work email address",
        variant: "destructive",
      });
      return;
    }

    // If company name is not provided, use first name + last name
    const submissionData = {
      ...data,
      companyName: data.companyName && data.companyName.trim()
        ? data.companyName
        : `${data.firstName} ${data.lastName}`,
      // Accepting an invite: the server joins the inviting org instead of
      // creating a new one and ignores companyName.
      ...(invite ? { inviteToken: invite.token, email: invite.email } : {}),
      ...(captchaTokenRef.current ? { captchaToken: captchaTokenRef.current } : {}),
      // Recorded, never used to refuse anyone here — same posture as every
      // other signal on this path until it has been measured against real
      // paying customers.
      ...(fingerprintData?.visitorId ? { deviceFingerprint: fingerprintData.visitorId } : {}),
      signupSource: invite ? 'invite' : 'register',
    };
    registerMutation.mutate(submissionData);
  };

  // Non-invite signup gets the one-field flow. Invitees fall through to the
  // full form below, which joins them to the org that invited them.
  if (!invite) {
    const googleIcon = (
      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
      </svg>
    );
    return (
      <TrialShell
        onBack={flow === 'otp' ? () => { setFlow('email'); setOtp(''); } : null}
        backLabel="Use a different email"
        headerRight={
          <Link href="/auth/login" className="lp-link lp-small" data-testid="link-login">Sign in</Link>
        }
      >
        {googleMutation.isPending ? (
          <div className="lp-panel lp-center" data-testid="text-google-finishing">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#E6ECE8] border-t-[#0B241B] mx-auto" />
            <p className="lp-muted mt-3">Setting up your account…</p>
          </div>
        ) : flow === 'otp' ? (
          <>
            <StepHead
              title="Check your email"
              sub={signupEmail.trim()
                ? <>We sent a 6-digit code to <strong className="text-[#0B241B]">{signupEmail.trim()}</strong>. Tap the button in that email, or enter the code here. It is valid for 30 minutes.</>
                : <>Verifying your email…</>}
            />
            <div className="lp-panel lp-stack">
              <div>
                <label className="lp-label" htmlFor="otp">Verification code</label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && otp.length === 6) verifyMutation.mutate(undefined); }}
                  className="lp-input lp-otp w-full"
                  placeholder="000000"
                  data-testid="input-otp"
                />
              </div>
              <button
                type="button"
                className="lp-btn lp-btn-primary lp-btn-lg lp-btn-block"
                disabled={otp.length !== 6 || verifyMutation.isPending}
                onClick={() => verifyMutation.mutate(undefined)}
                data-testid="button-verify-otp"
              >
                {verifyMutation.isPending ? 'Verifying…' : 'Verify and continue'}
              </button>
              <div className="lp-center">
                <button
                  type="button"
                  className="lp-textlink"
                  disabled={resendMutation.isPending || resendIn > 0 || !pendingUserId}
                  onClick={() => resendMutation.mutate()}
                  data-testid="button-resend-otp"
                >
                  {resendMutation.isPending
                    ? 'Sending…'
                    : resendIn > 0
                      ? `Resend available in ${resendIn}s`
                      : "Didn't get it? Resend code"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <StepHead
              title="Create your account"
              sub={`Start your ${TRIAL_DAYS}-day free trial. No password needed to start.`}
            />
            {/* The number they picked on limephone.io, carried in the link.
                Re-checked (exact match) on the number step after signup. */}
            {linkNumber && (
              <div className="lp-summary mb-4" data-testid="banner-carried-number">
                <p className="lp-eyebrow">Your number</p>
                <p className="lp-price text-xl mt-1">{linkNumber.formatted}</p>
                <p className="lp-help mt-1">We'll confirm it's still available right after you sign up.</p>
              </div>
            )}
            <div className="lp-panel lp-stack">
              <button
                type="button"
                className="lp-btn lp-btn-google lp-btn-block"
                style={{ height: 52 }}
                onClick={() => { window.location.href = '/api/auth/google'; }}
                data-testid="button-continue-google"
              >
                {googleIcon}
                Continue with Google
              </button>

              <div className="lp-or">or</div>

              <div>
                <label className="lp-label" htmlFor="signup-email">Work email</label>
                <Input
                  id="signup-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !captchaWaiting) submitEmail(); }}
                  className="lp-input w-full"
                  data-testid="input-signup-email"
                />
              </div>

              {/* ⚠⚠ Must stay rendered: TURNSTILE_ENFORCE_PRODUCTS includes
                  limephone, so a tokenless email signup is refused (403). */}
              <Turnstile
                onToken={onCaptchaToken}
                resetKey={captchaResetKey}
                onError={onCaptchaError}
              />

              <button
                type="button"
                className="lp-btn lp-btn-primary lp-btn-lg lp-btn-block"
                disabled={startEmailMutation.isPending || captchaWaiting}
                onClick={submitEmail}
                data-testid="button-continue-email"
              >
                {startEmailMutation.isPending ? 'Sending code…' : captchaWaiting ? 'Checking…' : 'Continue with email'}
              </button>
              <p className="lp-help lp-center">
                We'll email you a 6-digit code. You'll set a password at the end.
              </p>
            </div>
            <p className="lp-help lp-center mt-4">
              By continuing you agree to our <a href="/terms" className="lp-link">Terms</a> and{' '}
              <a href="/privacy" className="lp-link">Privacy Policy</a>.
            </p>
          </>
        )}
      </TrialShell>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-white flex flex-col lg:flex-row">
      {/* Left Side - Benefits & Social Proof */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-700 p-8 xl:p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="relative z-10">
          <Link href="/" data-testid="link-logo-desktop">
            <div className="flex items-center space-x-3 cursor-pointer group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform shadow-lg">
                <PhoneIcon className="w-7 h-7 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-white">
                LimePhone
              </span>
            </div>
          </Link>
        </div>

        <div className="relative z-10 space-y-8 xl:space-y-10">
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4 xl:mb-6">
              Transform how you communicate with customers
            </h2>
            <div className="flex items-center gap-2 mb-6 xl:mb-8" data-testid="text-rating">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <StarIcon key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-blue-100 text-base xl:text-lg">4.9/5 • Trusted by 6,000+ users</span>
            </div>
          </div>

          <div className="space-y-4 xl:space-y-5">
            <div className="flex items-start gap-4" data-testid="benefit-ai-automation">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle2Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">AI-Powered Communications</h3>
                <p className="text-blue-100 text-sm xl:text-base">Smart automation that helps you close deals faster</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="benefit-all-in-one">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle2Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">Everything In One Place</h3>
                <p className="text-blue-100 text-sm xl:text-base">Voice, SMS, CRM, and analytics unified in one dashboard</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="benefit-compliant">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle2Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">Enterprise-Grade Security</h3>
                <p className="text-blue-100 text-sm xl:text-base">Built-in compliance for A2P 10DLC, TCPA, and GDPR</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-3 text-blue-100 text-sm">
          <ShieldCheckIcon className="w-5 h-5" />
          <span>Bank-level encryption • SOC 2 Type II certified</span>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 xl:p-12">
        <div className="w-full max-w-lg">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-6 sm:mb-8">
            <Link href="/" data-testid="link-logo-mobile">
              <div className="flex items-center space-x-3 cursor-pointer">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <PhoneIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold bg-blue-600 bg-clip-text text-transparent">
                  LimePhone
                </span>
              </div>
            </Link>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="space-y-2 sm:space-y-3 text-center lg:text-left">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                {invite ? `Join ${invite.organizationName}` : `Start your ${TRIAL_DAYS}-day free trial`}
              </h1>
              <p className="text-slate-600 text-base sm:text-lg">
                {invite ? "Create your account to join the team." : "Get your business phone set up in under 2 minutes."}
              </p>
            </div>

            {/* Selected Number Display */}
            {selectedNumber && (
              <Alert className="border-blue-200 bg-blue-50">
                <PhoneIcon className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-xs sm:text-sm text-blue-800">
                  <strong>Your selected number:</strong> {selectedNumber}
                  {selectedCountry && <span className="ml-2">({selectedCountry})</span>}
                </AlertDescription>
              </Alert>
            )}

            {/* Mobile Social Proof */}
            <div className="lg:hidden flex items-center justify-center gap-2 text-xs sm:text-sm" data-testid="text-mobile-social-proof">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-slate-600">4.9/5 • 6,000+ users</span>
            </div>

            <Card className="border-0 shadow-xl">
              <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
                  {invite && (
                    <Alert className="border-blue-200 bg-blue-50">
                      <AlertDescription className="text-sm text-slate-700">
                        You're joining <strong>{invite.organizationName}</strong> as a <strong>{invite.role}</strong>. Create your account below to accept.
                      </AlertDescription>
                    </Alert>
                  )}
                  {inviteError && (
                    <Alert variant="destructive">
                      <AlertDescription className="text-sm">{inviteError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="firstName" className="text-xs sm:text-sm font-medium text-slate-700">
                        First name
                      </Label>
                      <Input
                        id="firstName"
                        placeholder="John"
                        autoFocus
                        {...form.register("firstName")}
                        className={`h-11 sm:h-12 text-base ${form.formState.errors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        data-testid="input-firstname"
                      />
                      {form.formState.errors.firstName && (
                        <p className="text-xs sm:text-sm text-red-500">
                          {form.formState.errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="lastName" className="text-xs sm:text-sm font-medium text-slate-700">
                        Last name
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        {...form.register("lastName")}
                        className={`h-11 sm:h-12 text-base ${form.formState.errors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        data-testid="input-lastname"
                      />
                      {form.formState.errors.lastName && (
                        <p className="text-xs sm:text-sm text-red-500">
                          {form.formState.errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="email" className="text-xs sm:text-sm font-medium text-slate-700">
                      Work email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      readOnly={!!invite}
                      {...form.register("email")}
                      className={`h-11 sm:h-12 text-base ${invite ? "bg-slate-100 cursor-not-allowed" : ""} ${
                        form.formState.errors.email || (!invite && emailValidation && !emailValidation.valid)
                          ? "border-red-500 focus-visible:ring-red-500"
                          : emailValidation?.valid && !emailValidation.checking
                          ? "border-blue-500"
                          : ""
                      }`}
                      data-testid="input-email"
                    />
                    {!invite && emailValidation?.checking && (
                      <p className="text-xs sm:text-sm text-slate-500">
                        Checking email...
                      </p>
                    )}
                    {!invite && emailValidation && !emailValidation.valid && !emailValidation.checking && (
                      <p className="text-xs sm:text-sm text-red-500">
                        {emailValidation.message || "Please use a work email address"}
                      </p>
                    )}
                    {form.formState.errors.email && (
                      <p className="text-xs sm:text-sm text-red-500">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Company name creates a new org — irrelevant to an invitee
                      joining an existing team, and the server ignores it for them. */}
                  {!invite && (
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="companyName" className="text-xs sm:text-sm font-medium text-slate-700">
                        Company name <span className="text-slate-400 text-xs">(optional)</span>
                      </Label>
                      <Input
                        id="companyName"
                        placeholder="Acme Business"
                        {...form.register("companyName")}
                        className="h-11 sm:h-12 text-base"
                        data-testid="input-company"
                      />
                    </div>
                  )}

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="password" className="text-xs sm:text-sm font-medium text-slate-700">
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        {...form.register("password")}
                        className={`h-11 sm:h-12 text-base pr-12 ${form.formState.errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        data-testid="input-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="button-toggle-password"
                      >
                        {showPassword ? (
                          <EyeOffIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                        ) : (
                          <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                        )}
                      </Button>
                    </div>
                    {form.formState.errors.password && (
                      <p className="text-xs sm:text-sm text-red-500">
                        {form.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="confirmPassword" className="text-xs sm:text-sm font-medium text-slate-700">
                      Confirm password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        {...form.register("confirmPassword")}
                        className={`h-11 sm:h-12 text-base pr-12 ${form.formState.errors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        data-testid="input-confirm-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        data-testid="button-toggle-confirm-password"
                      >
                        {showConfirmPassword ? (
                          <EyeOffIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                        ) : (
                          <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                        )}
                      </Button>
                    </div>
                    {form.formState.errors.confirmPassword && (
                      <p className="text-xs sm:text-sm text-red-500">
                        {form.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  {/* Credit Card Verification Note */}
                  <Alert className="border-blue-200 bg-blue-50" data-testid="alert-credit-card-notice">
                    <ShieldCheckIcon className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-xs sm:text-sm text-slate-700">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-900">To prevent fraud and abuse, we require:</p>
                        <p className="font-medium text-blue-700">✓ Valid Credit Card Verification (No charge during trial)</p>
                        <p className="text-slate-600">Once verified, your {TRIAL_DAYS}-day trial will be instantly activated.</p>
                        <p className="text-slate-600 font-medium">Cancel Anytime</p>
                      </div>
                    </AlertDescription>
                  </Alert>

                  <Turnstile
                    onToken={onCaptchaToken}
                resetKey={captchaResetKey}
                    onError={onCaptchaError}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 sm:h-14 bg-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all"
                    disabled={registerMutation.isPending || captchaWaiting}
                    data-testid="button-signup"
                  >
                    {registerMutation.isPending ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        <span className="text-sm sm:text-base">Creating your account...</span>
                      </div>
                    ) : (
                      <>
                        <span>Start {TRIAL_DAYS}-day free trial</span>
                        <ZapIcon className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-slate-500 px-2 sm:px-4 leading-relaxed">
                    By signing up, you agree to our <a href="/terms" className="underline hover:text-slate-700 font-medium">Terms of Service</a> and <a href="/privacy" className="underline hover:text-slate-700 font-medium">Privacy Policy</a>
                  </p>
                </form>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-slate-600 pt-2">
              Already have an account?{" "}
              <Link href="/auth/login" className="font-semibold text-blue-600 hover:text-blue-700" data-testid="link-signin">
                Sign in
              </Link>
            </p>

            {/* Trust Signals - Optimized for Mobile */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs text-slate-600 pt-2">
              <div className="flex items-center gap-1.5 justify-center" data-testid="trust-no-charges">
                <CheckCircle2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                <span>No charges</span>
              </div>
              <div className="flex items-center gap-1.5 justify-center" data-testid="trust-no-contract">
                <CheckCircle2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                <span>No contract</span>
              </div>
              <div className="flex items-center gap-1.5 justify-center" data-testid="trust-instant-setup">
                <CheckCircle2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                <span>Instant setup</span>
              </div>
              <div className="flex items-center gap-1.5 justify-center" data-testid="trust-cancel-anytime">
                <CheckCircle2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
